
import React, { useState, useMemo, useEffect } from 'react';
import { useAppointments } from './hooks/useAppointments';
import { useGroupRegistrations } from './hooks/useGroupRegistrations';
import { AppointmentList } from './appointments/AppointmentList';
import { GroupSessionCard } from './appointments/GroupSessionCard';
import { RefundRequestModal } from '@/components/dashboard/RefundRequestModal';
import { Button } from '@/components/ui/button';
import { RefreshCcw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import ErrorBoundary from './ErrorBoundary';

interface UpcomingAppointmentsProps {
  unreadByBooking?: Record<string, number>;
  onUnreadCountChange?: (count: number) => void;
  onRefreshUnreadCounts?: () => void;
}

const UpcomingAppointments: React.FC<UpcomingAppointmentsProps> = ({
  unreadByBooking = {},
  onUnreadCountChange,
  onRefreshUnreadCounts
}) => {
  const { t } = useTranslation('dashboard');
  const { appointments, isLoading, refreshAppointments } = useAppointments();
  const { upcoming: upcomingGroupSessions, loading: groupLoading, refresh: refreshGroups } = useGroupRegistrations();
  const [selectedAppointmentForRefund, setSelectedAppointmentForRefund] = useState<string | null>(null);

  // Calculate total unread count for upcoming appointments only
  const upcomingUnreadCount = useMemo(() => {
    return appointments.reduce((sum, appointment) => {
      return sum + (unreadByBooking[appointment.id] || 0);
    }, 0);
  }, [appointments, unreadByBooking]);

  // Notify parent component when unread count changes
  useEffect(() => {
    if (onUnreadCountChange) {
      onUnreadCountChange(upcomingUnreadCount);
    }
  }, [upcomingUnreadCount, onUnreadCountChange]);

  const handleRefresh = () => {
    refreshAppointments();
    refreshGroups();
  };

  // Sort group sessions by event_date for display
  const sortedGroupSessions = useMemo(() => {
    return [...upcomingGroupSessions].sort(
      (a, b) => new Date(a.event.event_date).getTime() - new Date(b.event.event_date).getTime()
    );
  }, [upcomingGroupSessions]);

  return (
    <ErrorBoundary>
      <div>
      <div className="flex items-center justify-between mb-4 sm:mb-6 gap-2 min-w-0">
        <h2 className="text-xl sm:text-2xl font-medium truncate">Upcoming</h2>
        <Button 
          variant="outline" 
          size="icon" 
          onClick={handleRefresh} 
          title={t('refreshAppointments')}
          aria-label={t('refreshAppointments')}
          className="h-8 w-8 shrink-0"
        >
          <RefreshCcw className="h-4 w-4" />
        </Button>
      </div>
      
      <AppointmentList
        appointments={appointments}
        isLoading={isLoading}
        onRequestRefund={(id) => setSelectedAppointmentForRefund(id)}
        unreadByBooking={unreadByBooking}
        onRefreshUnreadCounts={onRefreshUnreadCounts}
      />

      {/* Upcoming Group Sessions */}
      {!groupLoading && sortedGroupSessions.length > 0 && (
        <div className="mt-6">
          <h3 className="text-lg font-medium mb-3">{t('groupSessions.title', { defaultValue: 'Group Sessions' })}</h3>
          <div className="grid gap-3 sm:gap-4 lg:gap-6 grid-cols-1 xl:grid-cols-2">
            {sortedGroupSessions.map(reg => (
              <GroupSessionCard
                key={reg.id}
                registration={reg}
                showChat
                showCancel
                onRefresh={refreshGroups}
              />
            ))}
          </div>
        </div>
      )}
      
      {selectedAppointmentForRefund && (
        <RefundRequestModal
          isOpen={!!selectedAppointmentForRefund}
          onClose={() => setSelectedAppointmentForRefund(null)}
          bookingId={selectedAppointmentForRefund}
        />
      )}
      </div>
    </ErrorBoundary>
  );
};

export default UpcomingAppointments;
